'use client';

/**
 * Rozhovor v realnom case - rec na rec cez WebRTC.
 *
 * Nahradza retazec nahraj -> prepis -> rozmysli -> nahovor, ktory mal zmerane
 * hluche miesto 4,8 sekundy. Tu ide zvuk z mikrofonu priamo do modelu a spat,
 * takze odozva je pod sekundu, da sa modelu skakat do reci a model sam pozna,
 * ci clovek dohovoril alebo sa len nadychol.
 *
 * Zvuk NETECIE cez nas server - prehliadac sa spaja priamo s OpenAI kratkodobym
 * tokenom. Server vydava len ten token, kluc zostava uňho.
 *
 * Model nema pristup k databaze. Ked chce hladat, zavola nastroj `hladaj`,
 * my ho vykoname proti nasmu /search a vysledok mu posleme spat. Kriteria,
 * dataset aj okolie tak zostavaju tam, kde boli.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VOX_API, type VoxCriteria, type VoxListing, type VoxMessage } from './coreTypes';

export type VoxPhase = 'idle' | 'connecting' | 'listening' | 'thinking' | 'speaking';

const BARS = 28;

/** Zlucenie zmien do kriterii. Rozsahy a lokalita do hlbky, null = odobrat. */
function zluc(stav: VoxCriteria, zmena: Record<string, unknown>): VoxCriteria {
    const out: Record<string, unknown> = { ...stav };
    Object.keys(zmena || {}).forEach((k) => {
        const v = zmena[k];
        if (v === null) return void delete out[k];
        if (v === undefined) return;
        const objekt = (x: unknown) => x && typeof x === 'object' && !Array.isArray(x);
        if (objekt(v) && objekt(out[k])) {
            const spojene = { ...(out[k] as Record<string, unknown>) };
            Object.keys(v as Record<string, unknown>).forEach((kk) => {
                const vv = (v as Record<string, unknown>)[kk];
                if (vv === null) delete spojene[kk];
                else spojene[kk] = vv;
            });
            out[k] = spojene;
            return;
        }
        out[k] = v;
    });
    return out as VoxCriteria;
}

export function useVoxRealtime() {
    const [phase, setPhase] = useState<VoxPhase>('idle');
    const [messages, setMessages] = useState<VoxMessage[]>([]);
    const [criteria, setCriteria] = useState<VoxCriteria>({});
    const [items, setItems] = useState<VoxListing[]>([]);
    const [total, setTotal] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [started, setStarted] = useState(false);

    const pc = useRef<RTCPeerConnection | null>(null);
    const dc = useRef<RTCDataChannel | null>(null);
    const stream = useRef<MediaStream | null>(null);
    const audioEl = useRef<HTMLAudioElement | null>(null);

    // Hlasitost a spektrum pre vizual - v refoch, aby ich citanie kazdy snimok
    // neprekreslovalo stranku.
    const levelRef = useRef(0);
    const barsRef = useRef<number[]>(new Array(BARS).fill(0));
    const audioCtx = useRef<AudioContext | null>(null);
    const raf = useRef<number | null>(null);

    const criteriaRef = useRef<VoxCriteria>({});
    const relacia = useRef(0);

    const posli = useCallback((udalost: Record<string, unknown>) => {
        if (dc.current && dc.current.readyState === 'open') dc.current.send(JSON.stringify(udalost));
    }, []);

    /** Vykonanie nastroja `hladaj` a odoslanie vysledku spat modelu. */
    const vykonajHladaj = useCallback(
        async (callId: string, args: Record<string, unknown>) => {
            const { odznova, ...zmena } = args || {};
            const zaklad = odznova ? {} : criteriaRef.current;
            const nove = zluc(zaklad, zmena);
            criteriaRef.current = nove;
            setCriteria(nove);

            let odpoved: Record<string, unknown>;
            try {
                const r = await fetch(VOX_API + '/search', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ criteria: nove, limit: 12 }),
                });
                const data = await r.json();
                setItems(Array.isArray(data.items) ? data.items : []);
                setTotal(typeof data.total === 'number' ? data.total : 0);

                // Modelu davame zhrnutie, nie cely zoznam - musi z toho hovorit,
                // nie citat databazu.
                odpoved = {
                    pocet: data.total,
                    kriteria: nove,
                    ukazky: (data.items || []).slice(0, 3).map((x: VoxListing) => ({
                        mesto: x.city,
                        ulica: x.street,
                        cena: x.price,
                        plocha: x.area,
                    })),
                    ked_nic_nesedi: (data.relax || []).map((r: { hint: string; count: number }) => ({
                        navrh: r.hint,
                        pocet_potom: r.count,
                    })),
                };
            } catch {
                odpoved = { chyba: 'Vyhladavanie zlyhalo.' };
            }

            posli({
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: callId, output: JSON.stringify(odpoved) },
            });
            posli({ type: 'response.create' });
        },
        [posli],
    );

    /** Nastroj `detail` - odpoved na otazku o konkretnej ponuke. */
    const vykonajDetail = useCallback(
        async (callId: string, kod: string) => {
            let odpoved: unknown;
            try {
                const r = await fetch(VOX_API + '/listing-data/' + encodeURIComponent(kod));
                odpoved = r.ok ? await r.json() : { chyba: 'Ponuka sa nenašla.' };
            } catch {
                odpoved = { chyba: 'Detail sa nepodarilo načítať.' };
            }
            posli({
                type: 'conversation.item.create',
                item: { type: 'function_call_output', call_id: callId, output: JSON.stringify(odpoved) },
            });
            posli({ type: 'response.create' });
        },
        [posli],
    );

    /** Spracovanie udalosti z modelu. */
    const naUdalost = useCallback(
        (e: MessageEvent) => {
            let u: Record<string, unknown>;
            try {
                u = JSON.parse(e.data);
            } catch {
                return;
            }
            const typ = String(u.type || '');

            // Prepis toho, co povedal clovek
            if (typ === 'conversation.item.input_audio_transcription.completed') {
                const text = String(u.transcript || '').trim();
                if (text) setMessages((m) => [...m, { role: 'user', content: text }]);
                return;
            }

            // Co povedala Realika
            if (typ === 'response.output_audio_transcript.done' || typ === 'response.audio_transcript.done') {
                const text = String(u.transcript || '').trim();
                if (text) setMessages((m) => [...m, { role: 'assistant', content: text }]);
                return;
            }

            if (typ === 'input_audio_buffer.speech_started') {
                setPhase('listening');
                return;
            }
            if (typ === 'response.created') {
                setPhase('thinking');
                return;
            }
            if (typ === 'response.output_audio.delta') {
                setPhase('speaking');
                return;
            }
            if (typ === 'response.done') {
                setPhase('listening');
                return;
            }

            // Volanie nastroja
            if (typ === 'response.function_call_arguments.done') {
                const meno = String(u.name || '');
                let args: Record<string, unknown> = {};
                try {
                    args = JSON.parse(String(u.arguments || '{}'));
                } catch {
                    args = {};
                }
                if (meno === 'hladaj') void vykonajHladaj(String(u.call_id || ''), args);
                else if (meno === 'detail') void vykonajDetail(String(u.call_id || ''), String(args.kod || ''));
                return;
            }

            if (typ === 'error') {
                const spr = (u.error as { message?: string })?.message || 'Chyba spojenia.';
                console.warn('[realtime]', spr);
            }
        },
        [vykonajHladaj, vykonajDetail],
    );

    /** Meranie hlasitosti mikrofonu pre vlnu. */
    const meraj = useCallback((s: MediaStream) => {
        try {
            const Ctx =
                window.AudioContext ||
                (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
            const ctx = new Ctx();
            audioCtx.current = ctx;
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            analyser.smoothingTimeConstant = 0.75;
            ctx.createMediaStreamSource(s).connect(analyser);
            const freq = new Uint8Array(analyser.frequencyBinCount);
            const wave = new Uint8Array(analyser.fftSize);

            let last = 0;
            const tik = (t?: number) => {
                raf.current = requestAnimationFrame(tik);
                const now = t ?? 0;
                if (now - last < 33) return;
                last = now;

                analyser.getByteFrequencyData(freq);
                const usable = Math.floor(freq.length * 0.6);
                const per = Math.max(1, Math.floor(usable / BARS));
                const next: number[] = [];
                for (let i = 0; i < BARS; i++) {
                    let sum = 0;
                    for (let j = 0; j < per; j++) sum += freq[i * per + j] || 0;
                    next.push(Math.min(1, Math.pow(sum / per / 255, 0.75) * 1.6));
                }
                barsRef.current = next;

                analyser.getByteTimeDomainData(wave);
                let acc = 0;
                for (let i = 0; i < wave.length; i++) {
                    const d = (wave[i] - 128) / 128;
                    acc += d * d;
                }
                levelRef.current = Math.min(1, Math.sqrt(acc / wave.length) * 4);
            };
            raf.current = requestAnimationFrame(tik);
        } catch {
            /* vlna je ozdoba, rozhovor bezi dalej */
        }
    }, []);

    const stop = useCallback(() => {
        relacia.current++;
        if (raf.current !== null) {
            cancelAnimationFrame(raf.current);
            raf.current = null;
        }
        if (audioCtx.current) {
            audioCtx.current.close().catch(() => undefined);
            audioCtx.current = null;
        }
        if (dc.current) {
            try {
                dc.current.close();
            } catch {
                /* uz moze byt zavrety */
            }
            dc.current = null;
        }
        if (pc.current) {
            pc.current.getSenders().forEach((s) => s.track && s.track.stop());
            pc.current.close();
            pc.current = null;
        }
        if (stream.current) {
            stream.current.getTracks().forEach((t) => t.stop());
            stream.current = null;
        }
        if (audioEl.current) {
            audioEl.current.srcObject = null;
            audioEl.current.remove();
            audioEl.current = null;
        }
        levelRef.current = 0;
        barsRef.current = new Array(BARS).fill(0);
        setPhase('idle');
    }, []);

    const start = useCallback(async () => {
        if (phase !== 'idle') return;
        const moja = ++relacia.current;
        setError(null);
        setPhase('connecting');
        setStarted(true);

        try {
            const tokR = await fetch(VOX_API + '/realtime/token', { method: 'POST' });
            if (!tokR.ok) throw new Error('Nepodarilo sa začať rozhovor.');
            const { token } = await tokR.json();
            if (moja !== relacia.current) return;

            const s = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });
            if (moja !== relacia.current) {
                s.getTracks().forEach((t) => t.stop());
                return;
            }
            stream.current = s;
            meraj(s);

            const spoj = new RTCPeerConnection();
            pc.current = spoj;

            // Hlas Realiky - prehrava sa priamo z prichadzajuceho prudu
            const a = document.createElement('audio');
            a.autoplay = true;
            audioEl.current = a;
            spoj.ontrack = (ev) => {
                a.srcObject = ev.streams[0];
            };

            s.getTracks().forEach((t) => spoj.addTrack(t, s));

            const kanal = spoj.createDataChannel('oai-events');
            dc.current = kanal;
            kanal.onmessage = naUdalost;
            kanal.onopen = () => {
                if (moja === relacia.current) setPhase('listening');
            };

            const offer = await spoj.createOffer();
            await spoj.setLocalDescription(offer);

            const sdpR = await fetch('https://api.openai.com/v1/realtime/calls', {
                method: 'POST',
                body: offer.sdp,
                headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/sdp' },
            });
            if (!sdpR.ok) throw new Error('Spojenie sa nepodarilo nadviazať.');
            const answer = await sdpR.text();
            if (moja !== relacia.current) return;
            await spoj.setRemoteDescription({ type: 'answer', sdp: answer });
        } catch (e) {
            const name = (e as { name?: string })?.name || '';
            if (name === 'NotAllowedError')
                setError('Prehliadač prístup k mikrofónu nepovolil. Skontroluj povolenie pre túto stránku.');
            else setError((e as Error).message || 'Rozhovor sa nepodarilo spustiť.');
            stop();
        }
    }, [phase, meraj, naUdalost, stop]);

    const sendText = useCallback(
        (text: string) => {
            const t = String(text || '').trim();
            if (!t) return;
            setMessages((m) => [...m, { role: 'user', content: t }]);
            posli({
                type: 'conversation.item.create',
                item: { type: 'message', role: 'user', content: [{ type: 'input_text', text: t }] },
            });
            posli({ type: 'response.create' });
        },
        [posli],
    );

    const reset = useCallback(() => {
        stop();
        criteriaRef.current = {};
        setCriteria({});
        setMessages([]);
        setItems([]);
        setTotal(null);
        setError(null);
        setStarted(false);
    }, [stop]);

    useEffect(() => () => stop(), [stop]);

    const activeKeys = useMemo(
        () =>
            (Object.keys(criteria) as Array<keyof VoxCriteria>).filter((k) => {
                const v = criteria[k];
                if (v === null || v === undefined) return false;
                if (Array.isArray(v)) return v.length > 0;
                if (typeof v === 'object') return Object.keys(v).length > 0;
                return true;
            }),
        [criteria],
    );

    return {
        mode: phase,
        started,
        messages,
        criteria,
        activeKeys,
        items,
        total,
        relax: [] as { keys: string[]; label: string; count: number; hint: string }[],
        searching: false,
        error,
        getLevel: useCallback(() => levelRef.current, []),
        getBars: useCallback(() => barsRef.current, []),
        toggleMic: useCallback(() => (phase === 'idle' ? void start() : stop()), [phase, start, stop]),
        stop,
        ask: async (t: string) => sendText(t),
        removeCriterion: (k: keyof VoxCriteria) => {
            const next = { ...criteriaRef.current };
            delete next[k];
            criteriaRef.current = next;
            setCriteria(next);
        },
        applyRelax: () => undefined,
        reset,
        settings: null,
        applySettings: () => undefined,
    };
}
